import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  format,
  startOfToday,
  isWithinInterval,
  startOfDay,
  endOfDay,
  subHours,
  isToday,
  differenceInSeconds
} from 'date-fns';
import { CalendarHeader, CalendarViewSwitcher } from './components/calendar';
import { CalendarView } from './components/calendar/CalendarViewSwitcher';
import { useAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { translatedTasksAtom } from '../../atoms/v0/translationAtoms';
import { fetchTasksAtom, updateTaskAtom } from '../../atoms/v0/taskAtoms';
import { fetchProjectsAtom } from '../../atoms/v0/projectAtoms';
import { projectByIdAtom } from '../../atoms/v0/projectAtoms';
import { useTranslation } from 'react-i18next';
import { taskToEvents, hexToRgba } from '../../utils/calendarUtils';
import { trackCalendarEvents } from '../../lib/analyticsEnhanced';
import { Task, UpdateTaskData, CreateTaskData } from '../../types/task';
import { TaskList } from './components/tasks/TaskList';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { H as Highlight } from 'highlight.run';
import { useToast } from '@chakra-ui/react';
import { EventDropArg, DateSelectArg } from '@fullcalendar/core';

// 导入FullCalendar相关组件
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

// 使用loadable包装translatedTasksAtom
const loadableTranslatedTasksAtom = loadable(translatedTasksAtom);

export const Calendar: React.FC = () => {
  // 状态
  const [currentDate, setCurrentDate] = useState(startOfToday());
  const [selectedDate, setSelectedDate] = useState(startOfToday());
  // 检测是否为移动设备，用于初始化视图
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  // 统一使用3日视图作为默认视图（移动设备和桌面设备都使用3日视图）
  const [calendarView, setCalendarView] = useState<CalendarView>('timeGridThreeDay');
  // 跟踪用户是否手动切换过视图
  const [userChangedView, setUserChangedView] = useState(false);
  const calendarRef = useRef<FullCalendar | null>(null);
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null);
  const { t, i18n } = useTranslation(['v0/calendar', 'v0/common']);

  // 任务详情浮层状态
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  // 新任务初始数据
  const [newTaskInitialData, setNewTaskInitialData] = useState<Partial<CreateTaskData>>({});
  // 提交状态
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast通知
  const toast = useToast();

  // 获取任务和项目数据
  const [tasksLoadable] = useAtom(loadableTranslatedTasksAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const getProjectById = useAtom(projectByIdAtom)[0];

  // 初始化任务和项目 (以及添加挂载/卸载日志和 Highlight control)
  useEffect(() => {
    console.log('[Calendar.tsx] Component Mounted');
    try {
      console.log('[Calendar.tsx] Attempting to stop Highlight.run recording...');
      Highlight.stop();
      console.log('[Calendar.tsx] Highlight.run H.stop() called.');
    } catch (error) {
      console.error('[Calendar.tsx] Error calling Highlight.stop():', error);
    }

    fetchTasks();
    fetchProjects();

    // 清理函数，在组件卸载时调用
    return () => {
      console.log('[Calendar.tsx] Component Will Unmount. calendarRef.current is:', calendarRef.current);
      try {
        console.log('[Calendar.tsx] Attempting to start Highlight.run recording...');
        Highlight.start();
        console.log('[Calendar.tsx] Highlight.run H.start() called.');
      } catch (error) {
        console.error('[Calendar.tsx] Error calling Highlight.start():', error);
      }
    };
  }, [fetchTasks, fetchProjects, calendarRef]);

  // 转换任务为日历事件 - 使用 useMemo
  const events =
    useMemo(() => {
      if (tasksLoadable.state === 'hasData') {
        return tasksLoadable.data.flatMap(task => taskToEvents(task));
      }
      return [];
    }, [tasksLoadable]);

  // 处理上个月按钮点击 - 使用 useCallback
  const handlePrevMonth = useCallback(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().prev();
    }
  }, [calendarRef]);

  // 处理下个月按钮点击 - 使用 useCallback
  const handleNextMonth = useCallback(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().next();
    }
  }, [calendarRef]);

  // 处理今天按钮点击 - 使用 useCallback
  const handleTodayClick = useCallback(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().today();
    }
  }, [calendarRef]);

  // 处理日期点击 - 使用 useCallback
  const handleDateClick = useCallback((info: any) => {
    setSelectedDate(info.date);
  }, [setSelectedDate]);

  // 处理日期选择（长按创建任务）- 使用 useCallback
  const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
    // 只在周、3日、日视图中启用长按创建
    const isEnabledView = ['timeGridWeek', 'timeGridThreeDay', 'timeGridDay'].includes(selectInfo.view.type);
    if (!isEnabledView) {
      return;
    }

    const startDate = selectInfo.start;
    const endDate = selectInfo.end;

    // 计算选择的时间范围（秒）
    const durationInSeconds = differenceInSeconds(endDate, startDate);

    // 准备创建任务的初始数据
    const initialData: Partial<CreateTaskData> = {
      title: '',
      startAt: startDate.toISOString(),
      cost: durationInSeconds > 0 ? durationInSeconds : undefined,
    };

    // 设置初始数据并打开任务详情浮层（创建模式）
    setNewTaskInitialData(initialData);
    setSelectedTaskId(null); // 确保是创建模式
    setIsTaskDetailOpen(true);

    // 必须取消选择，否则会影响后续操作
    if (calendarRef.current) {
      calendarRef.current.getApi().unselect();
    }
  }, [calendarRef, setSelectedTaskId, setIsTaskDetailOpen, setNewTaskInitialData]);

  // 处理视图切换 - 使用 useCallback
  const handleViewChange = useCallback((view: CalendarView) => {
    // 记录日历视图切换事件
    trackCalendarEvents.calendarViewChanged(calendarView, view, {
      is_mobile: isMobile,
      user_initiated: true
    });

    // 标记用户已手动切换视图，防止自动切换覆盖用户选择
    setUserChangedView(true);
    setCalendarView(view);
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(view);
    }
  }, [calendarRef, setCalendarView, setUserChangedView, calendarView, isMobile]);

  // 处理事件点击 - 使用 useCallback
  const handleEventClick = useCallback((info: any) => {
    const task = info.event.extendedProps.task;
    if (task && task.id) {
      // 记录日历事件点击
      trackCalendarEvents.calendarEventClicked('task', task.id, {
        view_type: calendarView,
        is_completed: task.done,
        has_due_date: !!task.dueAt,
        is_vital: task.vital,
        is_urgent: task.urgent
      });

      setSelectedTaskId(task.id);
      setIsTaskDetailOpen(true);
    }
  }, [setSelectedTaskId, setIsTaskDetailOpen, calendarView]);

  // 处理任务点击 - 使用 useCallback
  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setIsTaskDetailOpen(true);
  }, [setSelectedTaskId, setIsTaskDetailOpen]);

  // 处理任务状态切换 - 使用 useCallback
  const handleToggleTask = useCallback((taskId: string, completed: boolean) => {
    if (tasksLoadable.state === 'hasData') {
      const task = tasksLoadable.data.find(t => t.id === taskId);
      if (task) {
        updateTask({
          id: taskId,
          done: completed,
        });
      }
    }
  }, [tasksLoadable, updateTask]);

  // 处理关闭任务详情浮层 - 使用 useCallback
  const handleCloseTaskDetail = useCallback(() => {
    setIsTaskDetailOpen(false);
    // 延迟清除任务ID和初始数据，确保关闭动画完成后再清除
    setTimeout(() => {
      setSelectedTaskId(null);
      setNewTaskInitialData({});
    }, 300);
  }, [setIsTaskDetailOpen, setSelectedTaskId, setNewTaskInitialData]);

  // 处理事件拖拽 - 使用 useCallback
  const handleEventDrop = useCallback(async (dropInfo: EventDropArg) => {
    const { event } = dropInfo;
    const task = event.extendedProps?.task as Task | undefined;

    if (!task) {
      console.warn("拖拽的事件没有关联的任务数据:", event);
      dropInfo.revert(); // 在日历UI中恢复更改
      return;
    }

    const newStartAt = event.start ? event.start.toISOString() : undefined;

    // 检查任务原本是否有cost值
    const hadOriginalCost = task.cost !== null && task.cost !== undefined;
    let newCost: number | undefined = undefined;

    // 只有在任务原本有cost值的情况下才更新cost
    if (hadOriginalCost && event.start && event.end) {
      newCost = differenceInSeconds(event.end, event.start);
      if (newCost <= 0) { // 确保持续时间为正值
        console.warn("拖拽导致事件持续时间为零或负值");
        newCost = undefined;
      }
    }

    // 准备更新数据
    const updateData: UpdateTaskData = {
      id: task.id,
      startAt: newStartAt,
      cost: newCost, // 只有在任务原本有cost时才更新
      // 注意：这里不更新dueAt，拖拽只影响开始时间和持续时间
    };

    setIsSubmitting(true); // 指示后台活动
    const success = await updateTask(updateData);
    setIsSubmitting(false);

    if (!success) {
      toast({
        title: t('v0/calendar:notifications.updateFailed', '更新失败'),
        description: t('v0/calendar:errors.dragDropFailed', '拖拽更新任务失败'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      dropInfo.revert(); // 失败时恢复更改
    } else {
      toast({
        title: t('v0/calendar:notifications.updateSuccess', '更新成功'),
        status: 'success',
        duration: 1500,
        isClosable: true,
        position: 'top',
      });
    }
  }, [updateTask, toast, t]);

  // 处理事件调整大小 - 使用 useCallback
  const handleEventResize = useCallback(async (resizeInfo: any) => {
    const { event } = resizeInfo;
    const task = event.extendedProps?.task as Task | undefined;

    if (!task) {
      console.warn("调整大小的事件没有关联的任务数据:", event);
      resizeInfo.revert(); // 在日历UI中恢复更改
      return;
    }

    // 当用户调整事件大小时，总是保存新的时长，无论任务原本是否有cost值
    let newCost: number | undefined = undefined;
    if (event.start && event.end) {
      newCost = differenceInSeconds(event.end, event.start);
      if (newCost <= 0) { // 确保持续时间为正值
        console.warn("调整大小导致事件持续时间为零或负值");
        newCost = undefined;
      }
    }

    // 准备更新数据
    const updateData: UpdateTaskData = {
      id: task.id,
      cost: newCost, // 更新cost
    };

    setIsSubmitting(true); // 指示后台活动
    const success = await updateTask(updateData);
    setIsSubmitting(false);

    if (!success) {
      toast({
        title: t('v0/calendar:notifications.updateFailed', '更新失败'),
        description: t('v0/calendar:errors.resizeFailed', '调整任务时长失败'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      resizeInfo.revert(); // 失败时恢复更改
    } else {
      toast({
        title: t('v0/calendar:notifications.updateSuccess', '更新成功'),
        status: 'success',
        duration: 1500,
        isClosable: true,
        position: 'top',
      });
    }
  }, [updateTask, toast, t]);

  // 处理日期范围变化 - 使用 useCallback
  const handleDatesSet = useCallback((dateInfo: { start: Date; end: Date; view: any }) => {
    // 更新当前日期
    setCurrentDate(dateInfo.start);

    // 在时间网格视图中滚动到合适的位置
    if (dateInfo.view.type.startsWith('timeGrid')) {
      // 滚动到当前时间前2小时的位置
      const now = new Date();
      const scrollTargetDate = subHours(now, 2);
      const targetScrollTime = scrollTargetDate.toTimeString().slice(0, 8); // 格式为 HH:MM:SS

      setTimeout(() => {
        if (calendarRef.current) {
          try {
            calendarRef.current.getApi().scrollToTime(targetScrollTime);
          } catch (error) {
            console.error("[Calendar] Error scrolling to time:", error);
          }
        }
      }, 100);
    }
  }, [setCurrentDate, calendarRef, subHours]); // subHours 是从 date-fns 导入的，是稳定的

  // 检测设备类型并在变化时更新视图（如果用户未手动切换过）
  useEffect(() => {
    const checkMobileAndUpdateView = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);

      // 只有在用户未手动切换视图的情况下，才根据设备类型自动切换视图
      // 现在统一使用3日视图，不再根据设备类型切换
      if (!userChangedView) {
        const newView: CalendarView = 'timeGridThreeDay';

        // 如果视图需要变化，且日历已初始化，则更新视图
        if (newView !== calendarView) {
          setCalendarView(newView);
          if (calendarRef.current) {
            calendarRef.current.getApi().changeView(newView);
          }
        }
      }
    };

    // 初始检查
    checkMobileAndUpdateView();

    // 监听窗口大小变化
    window.addEventListener('resize', checkMobileAndUpdateView);

    // 清理监听器
    return () => {
      window.removeEventListener('resize', checkMobileAndUpdateView);
    };
  }, [calendarView, userChangedView, calendarRef]);

  // 自定义事件渲染 - 使用 useCallback
  const renderEventContent = useCallback((eventInfo: any) => {
    const task = eventInfo.event.extendedProps.task;
    if (!task) return <i>{eventInfo.event.title}</i>;

    const isDueMarkerEvent = eventInfo.event.id.endsWith('-due');
    const eventTextColor = eventInfo.event.textColor || eventInfo.event.color || '#8E8E93';
    const isPastEvent = new Date(eventInfo.event.start) < new Date();
    const isMonthView = eventInfo.view.type === 'dayGridMonth';
    const isTimeGridView = eventInfo.view.type.startsWith('timeGrid');

    // Calculate border color and style for the inner wrapper
    let borderLeftStyle: string | undefined;
    if (task.vital && task.urgent) {
      borderLeftStyle = '3px solid #FF385C'; // Red
    } else if (task.vital) {
      borderLeftStyle = '3px solid #00A699'; // Teal/Orange
    } else if (task.urgent) {
      borderLeftStyle = '3px solid #FFB400'; // Yellow/Blue
    } else {
      const defaultBorderColor = hexToRgba(eventTextColor, 0.3);
      borderLeftStyle = `3px solid ${defaultBorderColor}`;
    }

    // Style for the outermost container (handles radius and clipping)
    const outerContainerStyle: React.CSSProperties = {
        borderRadius: '0.25rem',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        opacity: isPastEvent ? 0.5 : 1,
    };

    // Style for the inner wrapper (handles border and layout)
    const innerWrapperBaseStyle: React.CSSProperties = {
        borderLeft: borderLeftStyle,
        width: '100%',
        height: '100%',
    };

    const getCheckboxColor = () => {
      if (task.done) return 'border-ios-gray3 bg-ios-gray3 dark:border-ios-dark-gray3 dark:bg-ios-dark-gray3';
      if (task.vital && task.urgent) return 'border-ios-red dark:border-ios-dark-red';
      if (task.vital) return 'border-ios-orange dark:border-ios-dark-orange';
      if (task.urgent) return 'border-ios-blue dark:border-ios-dark-blue';
      return 'border-ios-gray3 dark:border-ios-dark-gray3';
    };

    // Month View
    if (isMonthView) {
      return (
        <div style={outerContainerStyle} className="cursor-pointer">
          <div
            style={innerWrapperBaseStyle}
            className={`flex items-center overflow-hidden px-1`}
          >
            {/* Conditional Checkbox/Pin Emoji */}
            {!isMobile && (
              isDueMarkerEvent ? (
                <div className="flex-shrink-0 w-3.5 h-3.5 mr-1.5 flex items-center justify-center text-sm">
                  📌
                </div>
              ) : (
                <div
                  className={`flex-shrink-0 w-3.5 h-3.5 rounded-full mr-1.5 border ${getCheckboxColor()} flex items-center justify-center`}
                >
                  {task.done && (
                    <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12L10 17L19 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              )
            )}
            <div
              className={`flex-1 overflow-hidden ${task.done ? 'line-through' : ''}`}
            >
              <div className="text-xs font-medium truncate" style={{ color: eventTextColor }}>
                {eventInfo.event.title}
              </div>
              {!eventInfo.event.allDay && (
                <div className="text-xs text-ios-gray dark:text-ios-dark-gray truncate">
                  {format(eventInfo.event.start, 'HH:mm')}
                  {eventInfo.event.end && ` - ${format(eventInfo.event.end, 'HH:mm')}`}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // TimeGrid View
    if (isTimeGridView) {
      return (
        <div style={outerContainerStyle} className="cursor-pointer">
          <div
            style={innerWrapperBaseStyle}
            className={`flex flex-col overflow-hidden p-1`}
          >
            <div className="flex items-center">
              {/* Conditional Checkbox/Pin Emoji */}
              {!isMobile && (
                isDueMarkerEvent ? (
                  <div className="flex-shrink-0 w-3.5 h-3.5 mr-1.5 flex items-center justify-center text-sm">
                    📌
                  </div>
                ) : (
                  <div
                    className={`flex-shrink-0 w-3.5 h-3.5 rounded-full mr-1.5 border ${getCheckboxColor()} flex items-center justify-center`}
                  >
                    {task.done && (
                      <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12L10 17L19 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                )
              )}
              <div className={`flex-1 font-medium text-xs ${task.done ? 'line-through' : ''} truncate`} style={{ color: eventTextColor }}>
                {eventInfo.event.title}
              </div>
            </div>
            {/* Time, Project, Notes... */}
            {!eventInfo.event.allDay && (
              <div className="text-xs text-ios-gray dark:text-ios-dark-gray mt-0.5 truncate">
                {format(eventInfo.event.start, 'HH:mm')} - {format(eventInfo.event.end, 'HH:mm')}
              </div>
            )}
            {task.projectId && (
              <div className="flex items-center mt-1 space-x-1">
                <svg className="w-2.5 h-2.5 text-ios-gray dark:text-ios-dark-gray" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span className="text-xs text-ios-gray dark:text-ios-dark-gray truncate">
                  {getProjectById(task.projectId)?.title || '项目'}
                </span>
              </div>
            )}
            {task.notes && (
              <div className="text-xs text-ios-gray dark:text-ios-dark-gray mt-0.5 truncate">
                {task.notes}
              </div>
            )}
          </div>
        </div>
      );
    }

    // List View
    return (
      <div style={{ opacity: isPastEvent ? 0.5 : 1 }} className="w-full cursor-pointer">
        <div
          style={innerWrapperBaseStyle}
          className={`flex items-center overflow-hidden px-1`}
        >
          {/* Conditional Checkbox/Pin Emoji */}
          {!isMobile && (
            isDueMarkerEvent ? (
              <div className="flex-shrink-0 w-3.5 h-3.5 mr-1.5 flex items-center justify-center text-sm">
                📌
              </div>
            ) : (
              <div
                className={`flex-shrink-0 w-3.5 h-3.5 rounded-full mr-1.5 border ${getCheckboxColor()} flex items-center justify-center`}
              >
                {task.done && (
                  <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12L10 17L19 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            )
          )}
          <div
            className={`flex-1 ${task.done ? 'line-through' : ''} truncate`}
            style={{
              paddingLeft: borderLeftStyle ? '0.25rem' : '0',
              color: eventTextColor,
              fontWeight: 500
            }}
          >
            {eventInfo.event.title}
          </div>
        </div>
      </div>
    );
  }, [isMobile, getProjectById, i18n.language, format, hexToRgba]); // 确保 format, hexToRgba 是稳定导入的

  // 新增：为事件添加类名的回调函数 - 使用 useCallback
  const getEventClassNames = useCallback((arg: any) => {
    let classNames = ['highlight-block'];
    // 你可以在这里根据 arg.event 的属性添加其他自定义类名，如果需要的话
    // 例如: if (arg.event.extendedProps.isSpecial) { classNames.push('special-event'); }
    return classNames;
  }, []);

  // 过滤选中日期的事件
  const selectedDateEvents = useMemo(() => events.filter(event => {
    if (!event.start || !event.end) return false;
    const selectedDayStart = startOfDay(selectedDate);
    const selectedDayEnd = endOfDay(selectedDate);
    return isWithinInterval(selectedDayStart, { start: event.start, end: event.end }) ||
           isWithinInterval(selectedDayEnd, { start: event.start, end: event.end }) ||
           isWithinInterval(event.start, { start: selectedDayStart, end: selectedDayEnd }) ||
           isWithinInterval(event.end, { start: selectedDayStart, end: selectedDayEnd });
  }), [events, selectedDate]);

  // 自定义事件渲染 - 使用 useCallback
  const dayCellClassNames = useCallback((arg: any) => {
    return isToday(arg.date) ? 'fc-day-today custom-today-cell' : '';
  }, [isToday]); // isToday 是稳定导入的

  const dayCellContent = useCallback((arg: any) => {
    const isCurrentDay = isToday(arg.date);
    // Only apply red background in month view
    const isMonthView = arg.view.type === 'dayGridMonth';
    const highlightClass = isCurrentDay && isMonthView
      ? 'bg-ios-red text-white font-medium'
      : '';

    return (
      <div className={`fc-daygrid-day-number flex items-center justify-center w-7 h-7 mx-auto rounded-full ${highlightClass}`}>
        {arg.dayNumberText}
      </div>
    );
  }, [isToday]); // isToday 是稳定导入的

  const dayHeaderContent = useCallback((arg: any) => {
    return (
      <div className="text-sm font-normal text-ios-gray dark:text-ios-dark-gray py-2">
        {arg.text.substring(0, 3)} {/* Use short day names */}
      </div>
    );
  }, []);

  return (
    <div className="pb-[calc(4rem+env(safe-area-inset-bottom))] font-ios pt-[env(safe-area-inset-top)]">
      {/* Add CSS override for today's background */}
      <style>
        {`
          .fc .fc-daygrid-day.fc-day-today {
            background-color: rgba(0, 122, 255, 0.05) !important; /* iOS Blue at 10% opacity */
          }
          .fc .fc-timegrid-col.fc-day-today {
            background-color: rgba(0, 122, 255, 0.05) !important; /* iOS Blue at 10% opacity */
          }
          /* Ensure other views don't get unwanted background if necessary */
          /* .fc-list .fc-day-today { background-color: transparent !important; } */

          /* 当前时间指示器样式 - 线条 */
          .fc .fc-timegrid-now-indicator-line {
            border-color: #FF3B30 !important; /* iOS Red */
            border-width: 0.09375rem !important;
            z-index: 4 !important;
            opacity: 0.8;
            box-shadow: 0 0 0.5rem rgba(255, 59, 48, 0.6); /* iOS Red glow effect */
          }

          /* 当前时间指示器样式 - 箭头/圆点 */
          .fc .fc-timegrid-now-indicator-arrow {
            border-color: #FF3B30 !important; /* iOS Red */
            border-radius: 50% !important;
            border-width: 0.3125rem !important;
            margin-top: -0.3125rem !important;
            right: 0 !important;
            left: auto !important;
          }

          /* 深色模式调整 */
          .dark .fc .fc-timegrid-now-indicator-line {
            border-color: #FF453A !important; /* iOS Dark Red */
            box-shadow: 0 0 0.5rem rgba(255, 69, 58, 0.4); /* iOS Dark Red glow */
          }

          .dark .fc .fc-timegrid-now-indicator-arrow {
            border-color: #FF453A !important; /* iOS Dark Red */
          }

          /* 修复深色模式下 FullCalendar 的白色边框问题 */
          .dark .fc table,
          .dark .fc th,
          .dark .fc td,
          .dark .fc .fc-scrollgrid,
          .dark .fc .fc-scrollgrid-section,
          .dark .fc .fc-scrollgrid-section table,
          .dark .fc .fc-scrollgrid-section th,
          .dark .fc .fc-scrollgrid-section td,
          .dark .fc .fc-scrollgrid-section-header,
          .dark .fc .fc-scrollgrid-section-sticky,
          .dark .fc .fc-timegrid-slots table,
          .dark .fc .fc-timegrid-slots th,
          .dark .fc .fc-timegrid-slots td,
          .dark .fc .fc-daygrid-body,
          .dark .fc .fc-daygrid-day,
          .dark .fc .fc-timegrid-col,
          .dark .fc .fc-col-header,
          .dark .fc .fc-col-header-cell {
            border-color: #3A3A3C !important; /* 使用深色边框 */
          }

          /* 特别针对头部边框 */
          .dark .fc .fc-scrollgrid-section-header .fc-scrollgrid-section-sticky,
          .dark .fc .fc-scrollgrid-section-header th,
          .dark .fc .fc-scrollgrid-section-header td {
            border-bottom-color: #3A3A3C !important;
            border-top-color: #3A3A3C !important;
          }

          /* 更简洁的方案：直接覆盖 FullCalendar 的 CSS 变量 */
          .dark .fc {
            --fc-border-color: #3A3A3C !important;
            --fc-button-border-color: #2c3e50 !important;
          }

          /* 强制覆盖头部的白色背景和边框 */
          .dark .fc .fc-scrollgrid-section-header {
            background-color: var(--chakra-colors-gray-800) !important;
          }

          .dark .fc .fc-scrollgrid-section-header .fc-scrollgrid-section-sticky {
            background-color: var(--chakra-colors-gray-800) !important;
            border-color: #3A3A3C !important;
          }

          /* 滚动容器背景色 */
          .dark .fc .fc-scroller-harness {
            background-color: #1c1c1e !important;
          }

          /* 事件的阴影效果调暗 */
          .dark .fc .fc-timegrid-event-harness-inset .fc-timegrid-event {
            box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1) !important;
          }
        `}
      </style>

      {/* 日历头部 */}
      <CalendarHeader
        currentDate={currentDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onTodayClick={handleTodayClick}
      />

      {/* 视图切换器 */}
      <div className="px-4 py-2">
        <CalendarViewSwitcher
          currentView={calendarView}
          onChange={handleViewChange}
          className="mb-2"
        />
      </div>

      {/* FullCalendar 日历 */}
      <div className="px-4 pb-4">
        <div
          ref={calendarWrapperRef}
          className="highlight-block bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray4 shadow-ios dark:shadow-ios-dark"
        >
          {tasksLoadable.state === 'loading' ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-ios-blue"></div>
            </div>
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={calendarView}
              views={{
                dayGridMonth: {
                  titleFormat: { year: 'numeric', month: 'long' },
                  dayMaxEventRows: 4,
                },
                timeGridWeek: {
                  titleFormat: { year: 'numeric', month: 'long', day: '2-digit' },
                },
                timeGridThreeDay: {
                  type: 'timeGrid',
                  duration: { days: 3 },
                  buttonText: '3日',
                  titleFormat: { year: 'numeric', month: 'long', day: '2-digit' },
                },
                timeGridDay: {
                  titleFormat: { year: 'numeric', month: 'long', day: '2-digit' },
                },
                listWeek: {
                  titleFormat: { year: 'numeric', month: 'long', day: 'numeric' },
                  duration: { weeks: 1 },
                  noEventsContent: t('v0/calendar:noEventsToday'),
                }
              }}
              headerToolbar={false} // 使用自定义头部
              events={events}
              editable={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              eventDisplay="block"
              eventContent={renderEventContent}
              eventClick={handleEventClick}
              eventClassNames={getEventClassNames}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              allDaySlot={true}
              firstDay={1}
              locale={i18n.language}
              dateClick={handleDateClick}
              select={handleDateSelect}
              longPressDelay={500} // 设置长按延迟为500毫秒
              selectLongPressDelay={500} // 同样为select设置
              datesSet={handleDatesSet}
              height="auto"
              selectMinDistance={5}
              nowIndicator={true}
              scrollTime="06:00:00"
              slotDuration="00:30:00"
              slotLabelInterval="01:00"
              dayCellClassNames={dayCellClassNames}
              dayCellContent={dayCellContent}
              dayHeaderContent={dayHeaderContent}
            />
          )}
        </div>
      </div>

      {/* 选中日期的事件列表 - 使用TaskList组件 */}
      <div className="p-0">
        <TaskList
          tasks={selectedDateEvents.map(event => event.task).filter((task): task is Task => !!task)}
          title={t('v3/calendar:selectedDateEvents', '{{date}} 的事件', {
            date: format(selectedDate, i18n.language === 'zh' ? 'yyyy年MM月dd日' : 'MMMM d, yyyy')
          })}
          color="text-ios-blue dark:text-ios-dark-blue"
          icon={
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 16H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          onToggleTask={handleToggleTask}
          onTaskClick={handleTaskClick}
        />
      </div>

      {/* 任务详情浮层 */}
      <TaskDetailSheet
        taskId={selectedTaskId}
        initialData={newTaskInitialData}
        isOpen={isTaskDetailOpen}
        onClose={handleCloseTaskDetail}
      />
    </div>
  );
};
